import React, { useState, useCallback } from 'react';
import { Select, TextInput } from 'flowbite-react';
import { HiSearch } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { useFetchSessionsQuery } from '../redux/api/sessionsApi';
import { SessionTable } from '../components/SessionTable/SessionTable';
import { SessionModal } from '../components/SessionModal/SessionModal';
import { PageHeader } from '../components/Shared/PageHeader';
import { CustomPagination } from '../components/Shared/CustomPagination/CustomPagination';
import { Loading } from '../components/Shared/Loading/Loading';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePagination } from '../hooks/usePagination';
import { ISessionWithStats } from '../common/interfaces/Session.interface';
import { SessionStatus } from '../common/enums/SessionStatus';
import { useAppDispatch } from '../redux/hooks/useAppDispatch';
import {
  setSelectedSession,
  openSessionModal,
} from '../redux/slices/sessionSlice';

export const SessionsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  usePageTitle(t('sessions.sessionManagement'));

  const { currentPage, setCurrentPage, size } = usePagination(10);
  const [statusFilter, setStatusFilter] = useState<SessionStatus | ''>('');
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading, isError, error, refetch } = useFetchSessionsQuery({
    page: currentPage,
    limit: size,
    status: statusFilter || undefined,
    search: searchTerm || undefined,
  });

  const handleCreateNew = useCallback(() => {
    dispatch(setSelectedSession(null));
    dispatch(openSessionModal({ isModalOpenedForAdd: true }));
  }, [dispatch]);

  const handleEdit = (session: ISessionWithStats) => {
    dispatch(setSelectedSession(session));
    dispatch(openSessionModal({ isModalOpenedForAdd: false }));
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status: SessionStatus | '') => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const sessions = data?.data || [];
  const totalPages = data?.meta?.pageCount || 1;

  if (isError) return <ErrorComponent error={error} onRetry={refetch} />;
  if (isLoading) return <Loading />;

  return (
    <>
      <div className="mb-8">
        <PageHeader
          title={t('sessions.sessionManagement')}
          buttonText={t('sessions.createSession')}
          buttonOnClick={handleCreateNew}
        />

        {/* Filters and Search */}
        <div className="mb-6 flex flex-wrap items-end gap-4">
          <div className="min-w-[200px] max-w-md flex-1">
            <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
              {t('sessions.search')}
            </label>
            <TextInput
              icon={HiSearch}
              placeholder={t('sessions.searchSessions')}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>

          <div className="min-w-[150px]">
            <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
              {t('sessions.filterByStatus')}
            </label>
            <Select
              value={statusFilter}
              onChange={(e) =>
                handleStatusFilterChange(e.target.value as SessionStatus | '')
              }
            >
              <option value="">{t('sessions.all')}</option>
              <option value={SessionStatus.ACTIVE}>{t('status.ACTIVE')}</option>
              <option value={SessionStatus.INACTIVE}>
                {t('status.INACTIVE')}
              </option>
            </Select>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      {/* Table */}
      <div className="my-4">
        <SessionTable sessions={sessions} onEdit={handleEdit} />
      </div>

      {/* Modal */}
      <SessionModal />
    </>
  );
};
